/**
 * PulseAI Engine™ visual variants - SVG/CSS based animated mascots.
 * No image generation required - fully scalable, animatable, lightweight.
 */
import React from "react";

// Variant A — Humanoid AI Robot (SVG face)
const RobotFace = ({ size = 200 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} className="pulseai-svg">
    <defs>
      <radialGradient id="robot-bg" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#1a1a2e" />
        <stop offset="100%" stopColor="#0a0a14" />
      </radialGradient>
      <linearGradient id="robot-face" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#c0c5cc" />
        <stop offset="50%" stopColor="#8890a0" />
        <stop offset="100%" stopColor="#4a4f5c" />
      </linearGradient>
      <filter id="robot-glow">
        <feGaussianBlur stdDeviation="2" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    <rect width="200" height="200" fill="url(#robot-bg)" />
    {/* Head shape */}
    <path
      d="M60 40 Q100 25 140 40 L145 110 Q145 150 100 165 Q55 150 55 110 Z"
      fill="url(#robot-face)"
      stroke="#9aa0b4"
      strokeWidth="1"
    />
    {/* Cyan eyes with pulse */}
    <g filter="url(#robot-glow)">
      <ellipse cx="82" cy="90" rx="9" ry="12" fill="#00ffff">
        <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx="118" cy="90" rx="9" ry="12" fill="#00ffff">
        <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
      </ellipse>
    </g>
    {/* Purple accent line on forehead */}
    <path
      d="M75 55 L125 55"
      stroke="#a855f7"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.9"
    >
      <animate attributeName="opacity" values="0.4;1;0.4" dur="3s" repeatCount="indefinite" />
    </path>
    {/* Mouth grille */}
    <g stroke="#5a6070" strokeWidth="1.5">
      <line x1="85" y1="130" x2="115" y2="130" />
      <line x1="88" y1="136" x2="112" y2="136" />
      <line x1="91" y1="142" x2="109" y2="142" />
    </g>
    {/* Side panels */}
    <circle cx="60" cy="100" r="3" fill="#00ffff" opacity="0.7" />
    <circle cx="140" cy="100" r="3" fill="#a855f7" opacity="0.7" />
  </svg>
);

// Variant B — Neural Network Brain
const NeuralBrain = ({ size = 200 }) => {
  const nodes = [
    [100, 40], [70, 60], [130, 60], [50, 90], [150, 90],
    [100, 100], [60, 130], [140, 130], [100, 155], [80, 170], [120, 170],
  ];
  const connections = [
    [0, 1], [0, 2], [1, 3], [2, 4], [1, 5], [2, 5], [3, 6], [4, 7],
    [5, 6], [5, 7], [6, 8], [7, 8], [8, 9], [8, 10], [5, 8], [3, 5], [4, 5],
  ];
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} className="pulseai-svg">
      <defs>
        <radialGradient id="brain-bg" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#1a0a2e" />
          <stop offset="100%" stopColor="#05000a" />
        </radialGradient>
        <radialGradient id="node-grad">
          <stop offset="0%" stopColor="#c4b5fd" />
          <stop offset="50%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#4c1d95" />
        </radialGradient>
        <filter id="brain-glow">
          <feGaussianBlur stdDeviation="3" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect width="200" height="200" fill="url(#brain-bg)" />
      {/* Connections */}
      <g stroke="#8b5cf6" strokeWidth="0.8" opacity="0.6">
        {connections.map(([a, b], i) => (
          <line
            key={i}
            x1={nodes[a][0]}
            y1={nodes[a][1]}
            x2={nodes[b][0]}
            y2={nodes[b][1]}
          >
            <animate
              attributeName="opacity"
              values="0.2;0.9;0.2"
              dur={`${2 + (i % 3)}s`}
              repeatCount="indefinite"
              begin={`${i * 0.1}s`}
            />
          </line>
        ))}
      </g>
      {/* Nodes */}
      <g filter="url(#brain-glow)">
        {nodes.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="4" fill="url(#node-grad)">
            <animate
              attributeName="r"
              values="3;6;3"
              dur={`${2 + (i % 3) * 0.5}s`}
              repeatCount="indefinite"
              begin={`${i * 0.2}s`}
            />
          </circle>
        ))}
      </g>
    </svg>
  );
};

// Variant C — PulseOwl Mascot (cute cartoon owl)
const PulseOwl = ({ size = 200 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} className="pulseai-svg">
    <defs>
      <linearGradient id="owl-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#a855f7" />
        <stop offset="100%" stopColor="#06b6d4" />
      </linearGradient>
      <linearGradient id="owl-body" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#6d28d9" />
        <stop offset="100%" stopColor="#4c1d95" />
      </linearGradient>
    </defs>
    <rect width="200" height="200" fill="url(#owl-bg)" opacity="0.15" />
    <circle cx="100" cy="100" r="85" fill="#f5f5f5" opacity="0.05" />
    {/* Body */}
    <ellipse cx="100" cy="115" rx="55" ry="65" fill="url(#owl-body)" />
    {/* Belly */}
    <ellipse cx="100" cy="125" rx="35" ry="45" fill="#e9d5ff" opacity="0.7" />
    {/* Wings */}
    <path d="M55 100 Q45 130 60 160 Q70 145 70 120 Z" fill="#4c1d95" />
    <path d="M145 100 Q155 130 140 160 Q130 145 130 120 Z" fill="#4c1d95" />
    {/* Ear tufts */}
    <path d="M70 55 L75 40 L80 58 Z" fill="#4c1d95" />
    <path d="M130 55 L125 40 L120 58 Z" fill="#4c1d95" />
    {/* Eyes (big glasses) */}
    <circle cx="82" cy="85" r="18" fill="white" stroke="#1e1b4b" strokeWidth="2.5" />
    <circle cx="118" cy="85" r="18" fill="white" stroke="#1e1b4b" strokeWidth="2.5" />
    {/* Bridge of glasses */}
    <line x1="100" y1="85" x2="100" y2="85" stroke="#1e1b4b" strokeWidth="2.5" />
    <path d="M100 85 L100 85" stroke="#1e1b4b" strokeWidth="2.5" />
    {/* Glass bridge */}
    <line x1="100" y1="82" x2="100" y2="88" stroke="#1e1b4b" strokeWidth="2" />
    <path
      d="M100 85 Q100 83 100 85"
      stroke="#1e1b4b"
      strokeWidth="2"
      fill="none"
    />
    <line x1="100" y1="85" x2="100" y2="85" />
    {/* Pupils — animated (looking around) */}
    <circle cx="82" cy="85" r="7" fill="#1e1b4b">
      <animate
        attributeName="cx"
        values="82;85;79;82"
        dur="4s"
        repeatCount="indefinite"
      />
    </circle>
    <circle cx="118" cy="85" r="7" fill="#1e1b4b">
      <animate
        attributeName="cx"
        values="118;121;115;118"
        dur="4s"
        repeatCount="indefinite"
      />
    </circle>
    {/* Eye shine */}
    <circle cx="80" cy="82" r="2" fill="white" />
    <circle cx="116" cy="82" r="2" fill="white" />
    {/* Beak */}
    <path d="M96 100 L100 110 L104 100 Z" fill="#fbbf24" />
    {/* Headphones */}
    <path
      d="M55 70 Q55 40 100 40 Q145 40 145 70"
      stroke="#06b6d4"
      strokeWidth="6"
      fill="none"
      strokeLinecap="round"
    />
    <ellipse cx="53" cy="75" rx="10" ry="14" fill="#06b6d4" />
    <ellipse cx="147" cy="75" rx="10" ry="14" fill="#06b6d4" />
    {/* Pulse icon on body */}
    <path
      d="M85 135 L90 135 L93 128 L97 142 L101 132 L105 138 L115 138"
      stroke="#06b6d4"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    >
      <animate attributeName="opacity" values="1;0.5;1" dur="1.5s" repeatCount="indefinite" />
    </path>
  </svg>
);

// Variant D — Pulse Orb (single glowing sphere)
const PulseOrb = ({ size = 200 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} className="pulseai-svg">
    <defs>
      <radialGradient id="orb-bg" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#0f0a1e" />
        <stop offset="100%" stopColor="#000000" />
      </radialGradient>
      <radialGradient id="orb-core" cx="40%" cy="40%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="20%" stopColor="#67e8f9" />
        <stop offset="50%" stopColor="#8b5cf6" />
        <stop offset="90%" stopColor="#4c1d95" />
        <stop offset="100%" stopColor="#1e0a4d" />
      </radialGradient>
      <filter id="orb-blur">
        <feGaussianBlur stdDeviation="4" />
      </filter>
    </defs>
    <rect width="200" height="200" fill="url(#orb-bg)" />
    {/* Outer glow */}
    <circle cx="100" cy="100" r="75" fill="#8b5cf6" opacity="0.2" filter="url(#orb-blur)">
      <animate attributeName="r" values="70;85;70" dur="3s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.15;0.35;0.15" dur="3s" repeatCount="indefinite" />
    </circle>
    {/* Rotating ring 1 */}
    <ellipse
      cx="100"
      cy="100"
      rx="70"
      ry="20"
      fill="none"
      stroke="#67e8f9"
      strokeWidth="1.5"
      opacity="0.6"
      transform="rotate(30 100 100)"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="30 100 100"
        to="390 100 100"
        dur="8s"
        repeatCount="indefinite"
      />
    </ellipse>
    {/* Rotating ring 2 */}
    <ellipse
      cx="100"
      cy="100"
      rx="80"
      ry="15"
      fill="none"
      stroke="#a855f7"
      strokeWidth="1"
      opacity="0.5"
      transform="rotate(-20 100 100)"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="-20 100 100"
        to="-380 100 100"
        dur="12s"
        repeatCount="indefinite"
      />
    </ellipse>
    {/* Core orb */}
    <circle cx="100" cy="100" r="45" fill="url(#orb-core)">
      <animate attributeName="r" values="43;47;43" dur="2s" repeatCount="indefinite" />
    </circle>
    {/* Highlight */}
    <ellipse cx="85" cy="85" rx="12" ry="8" fill="white" opacity="0.4" />
  </svg>
);

// Variant E — Human "Alex" (stylized portrait)
const AlexPersona = ({ size = 200 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} className="pulseai-svg">
    <defs>
      <linearGradient id="alex-bg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1f2937" />
        <stop offset="100%" stopColor="#0a0a0a" />
      </linearGradient>
      <linearGradient id="alex-skin" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#fcd9b8" />
        <stop offset="100%" stopColor="#c79878" />
      </linearGradient>
      <linearGradient id="alex-hair" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#3f2815" />
        <stop offset="100%" stopColor="#1a0f08" />
      </linearGradient>
      <filter id="alex-rim">
        <feGaussianBlur stdDeviation="2" />
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    <rect width="200" height="200" fill="url(#alex-bg)" />
    {/* Cyan rim light */}
    <circle cx="100" cy="100" r="80" fill="none" stroke="#06b6d4" strokeWidth="1" opacity="0.3" filter="url(#alex-rim)" />
    {/* Shoulders/turtleneck */}
    <path d="M30 200 Q30 150 100 150 Q170 150 170 200 Z" fill="#0a0a0a" />
    {/* Neck */}
    <rect x="88" y="130" width="24" height="25" fill="url(#alex-skin)" />
    {/* Face */}
    <ellipse cx="100" cy="105" rx="38" ry="45" fill="url(#alex-skin)" />
    {/* Hair */}
    <path
      d="M62 95 Q60 60 100 55 Q140 60 138 95 L135 75 Q130 65 100 63 Q70 65 65 75 Z"
      fill="url(#alex-hair)"
    />
    {/* Eyes */}
    <ellipse cx="87" cy="102" rx="4" ry="2.5" fill="#1a1a1a" />
    <ellipse cx="113" cy="102" rx="4" ry="2.5" fill="#1a1a1a" />
    {/* Eyebrows */}
    <path d="M80 94 Q87 91 94 93" stroke="#2a1a0a" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M106 93 Q113 91 120 94" stroke="#2a1a0a" strokeWidth="2" fill="none" strokeLinecap="round" />
    {/* Smile */}
    <path d="M88 122 Q100 132 112 122" stroke="#8a4a3a" strokeWidth="2" fill="none" strokeLinecap="round" />
    {/* Rim light on hair */}
    <path
      d="M62 95 Q60 60 100 55"
      stroke="#06b6d4"
      strokeWidth="1.5"
      fill="none"
      opacity="0.5"
      filter="url(#alex-rim)"
    />
  </svg>
);

// Variant F — Hybrid (Orb + Neural Network rays)
const HybridOrbNeural = ({ size = 200 }) => {
  const rays = Array.from({ length: 12 }).map((_, i) => {
    const angle = (i * 360) / 12;
    const rad = (angle * Math.PI) / 180;
    return {
      x1: 100 + 40 * Math.cos(rad),
      y1: 100 + 40 * Math.sin(rad),
      x2: 100 + 90 * Math.cos(rad),
      y2: 100 + 90 * Math.sin(rad),
      nx: 100 + 90 * Math.cos(rad),
      ny: 100 + 90 * Math.sin(rad),
    };
  });
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} className="pulseai-svg">
      <defs>
        <radialGradient id="hybrid-bg" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#150a2e" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>
        <radialGradient id="hybrid-orb" cx="40%" cy="40%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="30%" stopColor="#a5f3fc" />
          <stop offset="60%" stopColor="#a855f7" />
          <stop offset="100%" stopColor="#3b0764" />
        </radialGradient>
        <filter id="hybrid-glow">
          <feGaussianBlur stdDeviation="2" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect width="200" height="200" fill="url(#hybrid-bg)" />
      {/* Outer connecting ring */}
      <circle cx="100" cy="100" r="90" fill="none" stroke="#6366f1" strokeWidth="0.5" opacity="0.3" strokeDasharray="2 4" />
      {/* Neural rays */}
      <g stroke="#a855f7" strokeWidth="1" opacity="0.7" filter="url(#hybrid-glow)">
        {rays.map((r, i) => (
          <line key={i} x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2}>
            <animate
              attributeName="opacity"
              values="0.2;1;0.2"
              dur={`${2 + (i % 3) * 0.5}s`}
              repeatCount="indefinite"
              begin={`${i * 0.15}s`}
            />
          </line>
        ))}
      </g>
      {/* Outer nodes */}
      <g fill="#67e8f9" filter="url(#hybrid-glow)">
        {rays.map((r, i) => (
          <circle key={i} cx={r.nx} cy={r.ny} r="3">
            <animate
              attributeName="r"
              values="2;4;2"
              dur={`${2 + (i % 3) * 0.5}s`}
              repeatCount="indefinite"
              begin={`${i * 0.15}s`}
            />
          </circle>
        ))}
      </g>
      {/* Inner rotating ring */}
      <ellipse
        cx="100"
        cy="100"
        rx="55"
        ry="18"
        fill="none"
        stroke="#67e8f9"
        strokeWidth="1"
        opacity="0.5"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 100 100"
          to="360 100 100"
          dur="10s"
          repeatCount="indefinite"
        />
      </ellipse>
      {/* Core orb */}
      <circle cx="100" cy="100" r="35" fill="url(#hybrid-orb)">
        <animate attributeName="r" values="33;38;33" dur="2.5s" repeatCount="indefinite" />
      </circle>
      {/* Highlight */}
      <ellipse cx="90" cy="90" rx="8" ry="6" fill="white" opacity="0.5" />
    </svg>
  );
};

// Variant G — Aria: Cyberpunk AI Goddess (realistic female face with holo visor)
const AriaGoddess = ({ size = 200 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} className="pulseai-svg">
    <defs>
      <linearGradient id="aria-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1a0033" />
        <stop offset="50%" stopColor="#2d1b69" />
        <stop offset="100%" stopColor="#000000" />
      </linearGradient>
      <linearGradient id="aria-skin" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#f8c8dc" />
        <stop offset="50%" stopColor="#e5a0c4" />
        <stop offset="100%" stopColor="#8b5a9f" />
      </linearGradient>
      <linearGradient id="aria-hair" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#ff00ff" />
        <stop offset="50%" stopColor="#a855f7" />
        <stop offset="100%" stopColor="#1e1b4b" />
      </linearGradient>
      <linearGradient id="aria-visor" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#00ffff" stopOpacity="0.8" />
        <stop offset="50%" stopColor="#ff00ff" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#00ffff" stopOpacity="0.8" />
      </linearGradient>
      <filter id="aria-glow">
        <feGaussianBlur stdDeviation="1.8" />
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    <rect width="200" height="200" fill="url(#aria-bg)" />
    {/* Ambient glow behind */}
    <circle cx="100" cy="100" r="70" fill="#ff00ff" opacity="0.15" filter="url(#aria-glow)" />
    {/* Hair back layer */}
    <path
      d="M50 85 Q45 40 100 40 Q155 40 150 85 L152 120 Q148 160 135 175 Q125 165 132 140 L130 100 Q128 75 100 70 Q72 75 70 100 L68 140 Q75 165 65 175 Q52 160 48 120 Z"
      fill="url(#aria-hair)"
    />
    {/* Face */}
    <ellipse cx="100" cy="108" rx="36" ry="45" fill="url(#aria-skin)" />
    {/* Neck */}
    <path d="M86 145 L86 165 L114 165 L114 145 Z" fill="url(#aria-skin)" />
    {/* Shoulders/bodysuit */}
    <path d="M30 200 Q30 155 100 155 Q170 155 170 200 Z" fill="#1a0033" stroke="#ff00ff" strokeWidth="1.5" opacity="0.9" />
    {/* Circuit tattoo on cheek */}
    <g stroke="#00ffff" strokeWidth="0.8" fill="none" filter="url(#aria-glow)" opacity="0.9">
      <path d="M118 115 L128 115 L128 122 L135 122">
        <animate attributeName="opacity" values="0.4;1;0.4" dur="3s" repeatCount="indefinite" />
      </path>
      <circle cx="128" cy="115" r="1.5" fill="#00ffff" />
      <circle cx="135" cy="122" r="1.5" fill="#00ffff" />
      <path d="M72 120 L65 120 L65 128" stroke="#ff00ff">
        <animate attributeName="opacity" values="1;0.4;1" dur="3s" repeatCount="indefinite" />
      </path>
      <circle cx="65" cy="128" r="1.5" fill="#ff00ff" />
    </g>
    {/* Holographic visor */}
    <rect x="62" y="93" width="76" height="16" rx="2" fill="url(#aria-visor)" opacity="0.7" filter="url(#aria-glow)">
      <animate attributeName="opacity" values="0.6;0.9;0.6" dur="2.5s" repeatCount="indefinite" />
    </rect>
    {/* Visor scan line */}
    <line x1="62" y1="101" x2="138" y2="101" stroke="#ffffff" strokeWidth="0.5" opacity="0.9" />
    <line x1="62" y1="97" x2="138" y2="97" stroke="#00ffff" strokeWidth="0.4" opacity="0.6" />
    <line x1="62" y1="105" x2="138" y2="105" stroke="#ff00ff" strokeWidth="0.4" opacity="0.6" />
    {/* Visor data points */}
    <g fill="#ffffff">
      <circle cx="72" cy="101" r="0.8">
        <animate attributeName="opacity" values="1;0.2;1" dur="1.2s" repeatCount="indefinite" />
      </circle>
      <circle cx="128" cy="101" r="0.8">
        <animate attributeName="opacity" values="0.2;1;0.2" dur="1.2s" repeatCount="indefinite" />
      </circle>
    </g>
    {/* Nose hint */}
    <path d="M100 115 L97 125 Q100 128 103 125 Z" fill="#c09ab8" opacity="0.6" />
    {/* Lips with metallic gloss */}
    <path d="M88 135 Q100 142 112 135 Q108 138 100 139 Q92 138 88 135 Z" fill="#ff1493" />
    <path d="M92 134 Q100 132 108 134" stroke="#ffffff" strokeWidth="0.3" fill="none" opacity="0.7" />
    {/* Earring / tech node */}
    <circle cx="138" cy="118" r="2.5" fill="#00ffff" filter="url(#aria-glow)">
      <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
    </circle>
    {/* Forehead gem */}
    <polygon points="100,82 104,86 100,90 96,86" fill="#ff00ff" filter="url(#aria-glow)" />
    {/* Hair strands glow */}
    <path d="M55 90 Q60 75 75 70" stroke="#ff00ff" strokeWidth="0.8" fill="none" opacity="0.7" filter="url(#aria-glow)" />
    <path d="M145 90 Q140 75 125 70" stroke="#00ffff" strokeWidth="0.8" fill="none" opacity="0.7" filter="url(#aria-glow)" />
  </svg>
);

// Variant H — Cipher: The All-Seeing AI Eye (iris made of neural circuits)
const CipherEye = ({ size = 200 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} className="pulseai-svg">
    <defs>
      <radialGradient id="cipher-bg" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#0a0015" />
        <stop offset="100%" stopColor="#000000" />
      </radialGradient>
      <radialGradient id="cipher-iris" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#000000" />
        <stop offset="15%" stopColor="#0ea5e9" />
        <stop offset="50%" stopColor="#06b6d4" />
        <stop offset="85%" stopColor="#a855f7" />
        <stop offset="100%" stopColor="#3b0764" />
      </radialGradient>
      <radialGradient id="cipher-pupil" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#000000" />
        <stop offset="70%" stopColor="#1a0033" />
        <stop offset="100%" stopColor="#0ea5e9" />
      </radialGradient>
      <filter id="cipher-glow">
        <feGaussianBlur stdDeviation="2" />
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    <rect width="200" height="200" fill="url(#cipher-bg)" />
    {/* Outer energy glow */}
    <circle cx="100" cy="100" r="85" fill="#06b6d4" opacity="0.1" filter="url(#cipher-glow)">
      <animate attributeName="opacity" values="0.08;0.2;0.08" dur="3s" repeatCount="indefinite" />
    </circle>
    {/* Eye shape (almond) */}
    <path
      d="M30 100 Q100 40 170 100 Q100 160 30 100 Z"
      fill="#0a0a0a"
      stroke="#06b6d4"
      strokeWidth="1"
      opacity="0.9"
    />
    {/* Eye inner darkness */}
    <path
      d="M32 100 Q100 44 168 100 Q100 156 32 100 Z"
      fill="#050510"
    />
    {/* Iris */}
    <circle cx="100" cy="100" r="42" fill="url(#cipher-iris)" />
    {/* Circuit pattern in iris (12 radial lines) */}
    <g stroke="#67e8f9" strokeWidth="0.8" opacity="0.7" filter="url(#cipher-glow)">
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i * 30 * Math.PI) / 180;
        return (
          <line
            key={i}
            x1={100 + 15 * Math.cos(a)}
            y1={100 + 15 * Math.sin(a)}
            x2={100 + 40 * Math.cos(a)}
            y2={100 + 40 * Math.sin(a)}
          >
            <animate
              attributeName="opacity"
              values="0.3;1;0.3"
              dur={`${2 + (i % 3)}s`}
              repeatCount="indefinite"
              begin={`${i * 0.12}s`}
            />
          </line>
        );
      })}
    </g>
    {/* Circuit nodes */}
    <g fill="#00ffff" filter="url(#cipher-glow)">
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i * 45 * Math.PI) / 180;
        return (
          <circle
            key={i}
            cx={100 + 30 * Math.cos(a)}
            cy={100 + 30 * Math.sin(a)}
            r="1.2"
          >
            <animate
              attributeName="r"
              values="1;2.5;1"
              dur={`${2 + (i % 2)}s`}
              repeatCount="indefinite"
            />
          </circle>
        );
      })}
    </g>
    {/* Inner ring */}
    <circle cx="100" cy="100" r="25" fill="none" stroke="#a855f7" strokeWidth="0.6" strokeDasharray="2 3" opacity="0.7">
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 100 100"
        to="360 100 100"
        dur="20s"
        repeatCount="indefinite"
      />
    </circle>
    {/* Pupil */}
    <circle cx="100" cy="100" r="14" fill="url(#cipher-pupil)">
      <animate attributeName="r" values="12;16;12" dur="4s" repeatCount="indefinite" />
    </circle>
    {/* Pupil center point */}
    <circle cx="100" cy="100" r="3" fill="#000000" />
    {/* Catchlight (reflection) */}
    <ellipse cx="90" cy="88" rx="5" ry="3" fill="white" opacity="0.7" />
    <circle cx="108" cy="108" r="1.5" fill="white" opacity="0.5" />
    {/* Upper eyelid line */}
    <path d="M30 100 Q100 40 170 100" fill="none" stroke="#06b6d4" strokeWidth="1.5" opacity="0.8" />
    {/* Eyelashes suggestion */}
    <g stroke="#06b6d4" strokeWidth="0.6" opacity="0.6">
      <line x1="65" y1="60" x2="63" y2="54" />
      <line x1="80" y1="50" x2="79" y2="43" />
      <line x1="100" y1="45" x2="100" y2="38" />
      <line x1="120" y1="50" x2="121" y2="43" />
      <line x1="135" y1="60" x2="137" y2="54" />
    </g>
  </svg>
);

// Variant I — Nexus: Sacred Crystal AI Core (rotating obelisk/prism with geometry)
const NexusCrystal = ({ size = 200 }) => (
  <svg viewBox="0 0 200 200" width={size} height={size} className="pulseai-svg">
    <defs>
      <radialGradient id="nexus-bg" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#0a1128" />
        <stop offset="100%" stopColor="#000000" />
      </radialGradient>
      <linearGradient id="nexus-crystal-front" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.95" />
        <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.85" />
        <stop offset="100%" stopColor="#4c1d95" stopOpacity="0.95" />
      </linearGradient>
      <linearGradient id="nexus-crystal-side" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#67e8f9" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#0891b2" stopOpacity="0.8" />
      </linearGradient>
      <linearGradient id="nexus-crystal-shadow" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1e1b4b" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#0a0a2a" stopOpacity="0.95" />
      </linearGradient>
      <filter id="nexus-glow">
        <feGaussianBlur stdDeviation="3" />
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    <rect width="200" height="200" fill="url(#nexus-bg)" />
    {/* Ambient halo */}
    <circle cx="100" cy="100" r="80" fill="#a855f7" opacity="0.15" filter="url(#nexus-glow)">
      <animate attributeName="opacity" values="0.1;0.25;0.1" dur="3s" repeatCount="indefinite" />
    </circle>
    {/* Sacred geometry ring behind */}
    <g stroke="#67e8f9" strokeWidth="0.5" fill="none" opacity="0.4">
      <circle cx="100" cy="100" r="75" strokeDasharray="1 2">
        <animateTransform attributeName="transform" type="rotate" from="0 100 100" to="360 100 100" dur="30s" repeatCount="indefinite" />
      </circle>
      <polygon points="100,30 160,135 40,135" strokeDasharray="2 3">
        <animateTransform attributeName="transform" type="rotate" from="0 100 100" to="-360 100 100" dur="40s" repeatCount="indefinite" />
      </polygon>
      <polygon points="100,170 40,65 160,65" strokeDasharray="2 3">
        <animateTransform attributeName="transform" type="rotate" from="0 100 100" to="360 100 100" dur="40s" repeatCount="indefinite" />
      </polygon>
    </g>
    {/* Floating particles */}
    <g fill="#c4b5fd" filter="url(#nexus-glow)">
      <circle cx="40" cy="50" r="1">
        <animate attributeName="opacity" values="0;1;0" dur="3s" repeatCount="indefinite" />
      </circle>
      <circle cx="160" cy="70" r="1">
        <animate attributeName="opacity" values="1;0;1" dur="4s" repeatCount="indefinite" />
      </circle>
      <circle cx="30" cy="150" r="1.2">
        <animate attributeName="opacity" values="0.5;1;0.5" dur="2.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="170" cy="160" r="0.8">
        <animate attributeName="opacity" values="1;0.3;1" dur="3.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="50" cy="170" r="1">
        <animate attributeName="opacity" values="0;1;0" dur="2.8s" repeatCount="indefinite" />
      </circle>
    </g>
    {/* Crystal obelisk - right side face (darker) */}
    <polygon points="100,40 135,85 135,145 100,175" fill="url(#nexus-crystal-shadow)" />
    {/* Crystal obelisk - left side face (medium) */}
    <polygon points="100,40 65,85 65,145 100,175" fill="url(#nexus-crystal-side)" />
    {/* Crystal obelisk - front face (bright) */}
    <polygon points="100,40 100,175 100,175 100,40" fill="none" />
    <polygon points="78,70 122,70 115,160 85,160" fill="url(#nexus-crystal-front)" opacity="0.95">
      <animate attributeName="opacity" values="0.8;1;0.8" dur="2.5s" repeatCount="indefinite" />
    </polygon>
    {/* Top edge highlight */}
    <polygon points="100,40 65,85 100,95 135,85" fill="#e9d5ff" opacity="0.7" />
    {/* Inner energy core */}
    <ellipse cx="100" cy="115" rx="10" ry="22" fill="#ffffff" opacity="0.85" filter="url(#nexus-glow)">
      <animate attributeName="opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" />
      <animate attributeName="ry" values="18;26;18" dur="2s" repeatCount="indefinite" />
    </ellipse>
    {/* Crystal edges / facet lines */}
    <g stroke="#e9d5ff" strokeWidth="0.6" opacity="0.8">
      <line x1="100" y1="40" x2="100" y2="175" />
      <line x1="65" y1="85" x2="135" y2="85" />
      <line x1="65" y1="145" x2="135" y2="145" />
      <line x1="78" y1="70" x2="122" y2="70" />
      <line x1="85" y1="160" x2="115" y2="160" />
    </g>
    {/* Energy beams emanating */}
    <g stroke="#a855f7" strokeWidth="0.8" opacity="0.6" filter="url(#nexus-glow)">
      <line x1="100" y1="40" x2="100" y2="20">
        <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" />
      </line>
      <line x1="100" y1="175" x2="100" y2="195">
        <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
      </line>
    </g>
    {/* Top glow point */}
    <circle cx="100" cy="40" r="3" fill="#ffffff" filter="url(#nexus-glow)">
      <animate attributeName="r" values="2;4;2" dur="1.5s" repeatCount="indefinite" />
    </circle>
  </svg>
);

export const PULSEAI_VARIANTS = {
  a_robot: { label: "Humanoidná AI tvár", style: "Tech / Premium", Component: RobotFace },
  b_brain: { label: "Neurónový mozog", style: "Abstract / Tech", Component: NeuralBrain },
  c_owl: { label: "PulseOwl maskot", style: "Cartoon / Memorable", Component: PulseOwl },
  d_orb: { label: "Pulse Orb", style: "Minimal / Modern", Component: PulseOrb },
  e_human: { label: "Alex (AI persona)", style: "Human / Trustworthy", Component: AlexPersona },
  f_hybrid: { label: "Hybrid: Orb + Neural", style: "Premium Hybrid", Component: HybridOrbNeural },
  g_aria: { label: "Aria — Cyberpunk Bohyňa", style: "Realistic / Futuristic", Component: AriaGoddess },
  h_cipher: { label: "Cipher — Všetko-vidiace oko", style: "Iconic / Futuristic", Component: CipherEye },
  i_nexus: { label: "Nexus — Kryštálové jadro", style: "Sacred / Premium", Component: NexusCrystal },
};

export const PULSEAI_DESCRIPTIONS = {
  a_robot: "Futuristická robotická tvár s cyan očami a fialovými neon akcentmi. Premium B2B SaaS look.",
  b_brain: "3D neurónová sieť s fialovo-modrým glow. Reprezentuje AI inteligenciu bez ľudskej tváre.",
  c_owl: "Priateľská sova s headphones a okuliarmi. Vysoká memorabilita, B2C friendly.",
  d_orb: "Svetelná energetická guľa s rotujúcimi prstencami. Moderný minimalistický tech feel.",
  e_human: "Profesionálna ľudská AI persona. Personal touch, trust signal.",
  f_hybrid: "Pulse orb s neurónovými lúčmi vychádzajúcimi von. Kombinácia inteligencie a energie.",
  g_aria: "Cyberpunk AI bohyňa s holografickým vizorom a neon tattoo obvodmi. Inšpirované Ghost in the Shell + Cyberpunk 2077. Maximálne zapamätateľná, realistická + futuristická.",
  h_cipher: "Všetko-vidiace AI oko — iris z neurónových obvodov, pulzujúca zrenica. Ikonické, záhadné, nezabudnuteľné. Symbol inteligencie a pozornosti.",
  i_nexus: "Kryštálový obelisk s posvätnou geometriou a energetickým jadrom. Jedinečné — ako Monolith zo Space Odyssey stretávajúci sa s diamantom.",
};

export const PulseAIVisual = ({ variantKey, size = 200, className = "" }) => {
  const v = PULSEAI_VARIANTS[variantKey] || PULSEAI_VARIANTS.f_hybrid;
  const Cmp = v.Component;
  return (
    <div className={className}>
      <Cmp size={size} />
    </div>
  );
};

export default PulseAIVisual;
